import type { Metrics } from '@drawing2d/core';
import { t, type Lang } from '../i18n.js';

/**
 * The accounting stamp.
 *
 * This carries the sentence that separates this product from every other
 * PDF-to-DXF converter: **every operator in the PDF either became an entity or
 * was recorded as a quality issue, and nothing is unaccounted.** It is the honest
 * form of "nothing is missing" — not that everything converted, but that nothing
 * vanished uncounted — and the number that decides it is the last one.
 *
 * It used to be a progress bar with a sentence underneath, folded out of sight:
 * the least memorable rendering of the most distinctive fact on the page. So it
 * takes the form its audience already reads. A print goes out for 検図 and comes
 * back with the checker's accounting stamped on it, and this is that stamp: a
 * ruled block, the three figures set in the mono, and the rule closing when the
 * account reconciles.
 *
 * **It is the machine's accounting and is never dressed as anyone's approval.**
 * A stamp that implied a person had signed the drawing would be a lie told in a
 * visual language this audience trusts, which is the worst kind — so it is
 * labelled as the operator ledger, it carries no name, no date and no seal, and
 * the wording says what was counted rather than that anything was approved.
 */
export function Ledger({ metrics, lang }: { metrics: Metrics; lang: Lang }): JSX.Element {
  const { ledger, unaccounted } = metrics;
  const reconciled = unaccounted === 0;

  return (
    <section
      className={`stamp${reconciled ? ' reconciled' : ' short'}`}
      data-reconciled={reconciled}
    >
      <h2>{t('ledger', lang)}</h2>

      {/*
        Read as a row of figures rather than a sentence, because it is a row of
        figures. Each carries its own label so the block survives being read out
        or printed in greyscale — the arrow alone would not.
      */}
      <dl className="stamp-figures">
        {/*
          `observed` leads, because it is the figure the others are checked
          against — counted by its own pass over what the reader reported. The
          stamp used to lead with `total`, which was *defined* as
          consumed + recorded and so agreed with them by construction: three
          figures that could never disagree, presented as an account.

          Four figures and not five. The class breakdown — how many of those
          operators only set state and drew nothing — is real and is in
          `report.json`; on screen it made the row wrap and took the convert page
          over its two-screen budget, and it is the least load-bearing of the
          five. The claim is `unaccounted`.
        */}
        <div>
          <dt>{t('operators', lang)}</dt>
          <dd data-figure="observed">{ledger.observed}</dd>
        </div>
        <div>
          <dt>{t('consumed', lang)}</dt>
          <dd data-figure="consumed">{ledger.consumed}</dd>
        </div>
        <div>
          <dt>{t('recorded', lang)}</dt>
          <dd data-figure="recorded">{ledger.recorded}</dd>
        </div>
        <div className="stamp-claim">
          <dt>{t('unaccounted', lang)}</dt>
          <dd data-figure="unaccounted" className={reconciled ? 'ok' : 'bad'}>
            {unaccounted}
          </dd>
        </div>
      </dl>

      <p className="note">{t('ledgerExplain', lang)}</p>
    </section>
  );
}
